import React, { useEffect, useState } from 'react';
import { HiCheck, HiTrash } from 'react-icons/hi';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import ImageInput from './ImageInput';
import communityService from '../../../services/community/communityService';
import { UploadedPictureModel } from '../../../models/communityService.model';

/** Matches COMMUNITY_PICTURES_UPLOAD_LIMIT on the API. */
const COMMUNITY_PICTURES_UPLOAD_LIMIT = 250;

interface CommunityImageInputProps {
  image: string | undefined;
  setImage: (image: string | undefined) => void;
  setImageAction?: (action: 'UPLOAD' | 'DELETE') => void;
  className?: string;
  uploadOnSelect?: boolean;
}

export default function CommunityImageInput({
  image,
  setImage,
  setImageAction = () => {},
  className,
  uploadOnSelect = true,
}: CommunityImageInputProps): JSX.Element {
  const { t } = useTranslation();
  const [images, setImages] = useState<UploadedPictureModel[]>([]);

  useEffect(() => {
    communityService.getPicturesForCommunity().then(setImages);
  }, []);

  const handlePictureOpload = async (picture: string | undefined) => {
    if (!picture) {
      setImage(undefined);
      return;
    }

    if (!uploadOnSelect) {
      setImage(picture);
      return;
    }

    const file = await fetch(picture).then((res) => res.blob() as Promise<File>);
    communityService.uploadPicture(file).then((path) => {
      setImage(path.path);
      setImages([...images, path]);
    });
  };

  const atUploadLimit = images.length >= COMMUNITY_PICTURES_UPLOAD_LIMIT;

  return (
    <>
      <div className="flex flex-wrap gap-6">
        {!atUploadLimit && (
          <ImageInput
            iconClassName="size-12"
            className={`object-cover ${className}`}
            image={image}
            setImage={uploadOnSelect ? handlePictureOpload : setImage}
            setImageAction={setImageAction}
          />
        )}

        {images.map((p) => (
          <div
            key={p.id}
            className={`relative aspect-square rounded-[10px] ${className} ${
              image === p.path ? 'outline outline-2 outline-primary' : ''
            }`}>
            <div
              onClick={() => {
                if (image === p.path) {
                  setImage(undefined);
                  setImageAction?.('DELETE');
                } else {
                  setImage(p.path);
                  setImageAction?.('UPLOAD');
                }
              }}
              className="h-full w-full">
              <img
                key={p.id}
                src={p.path}
                className="h-full w-full cursor-pointer rounded-[8px] object-cover"
                alt={`cover-image-${p.id}`}
              />
            </div>
            <button
              type="button"
              onClick={() =>
                communityService.deletePicture(p.id).then(() => {
                  setImages(images.filter((i) => i.id !== p.id));
                  if (image === p.path) {
                    setImage(undefined);
                    setImageAction?.('DELETE');
                  }
                })
              }
              className="absolute -right-2 -top-2 rounded-full bg-secondary-200 p-1">
              <HiTrash className="h-4 w-4 text-primary-900" />
            </button>
            <div
              className={classNames(
                'absolute left-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-secondary-50/50',
                { '!bg-secondary-50': image === p.path },
              )}>
              {' '}
              {image === p.path && <HiCheck className="bg-primary-50" />}
            </div>
          </div>
        ))}
      </div>
      {atUploadLimit && (
        <p className="">{t('page.createEvent.fields.coverPhoto.maxPhotos')}</p>
      )}
    </>
  );
}
